import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import RevealText from "../components/RevealText";
import MediaImage from "../components/MediaImage";
import { projects as staticProjects, getProjectVideoIds } from "../lib/data";
import { useProjects } from "../hooks/useProjects";

const FILTERS = ["All", "F&B", "Fashion", "Textile", "Events", "Handicraft", "Healthcare", "Commercial", "Extra Sauce", "Photography", "Films"];

function scrollToPortfolioResults(gridEl, filtersEl) {
  if (!gridEl) return;

  const headerHeight = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--site-header-height"),
  ) || 72;
  const isDesktop = window.matchMedia("(min-width: 768px)").matches;
  const filterHeight = isDesktop ? (filtersEl?.offsetHeight || 0) : 0;
  const targetTop = gridEl.getBoundingClientRect().top + window.scrollY - headerHeight - filterHeight - 16;

  if (window.scrollY <= targetTop) return;

  if (window.__lenis) {
    window.__lenis.scrollTo(targetTop, { duration: 0.9 });
  } else {
    window.scrollTo({ top: targetTop, behavior: "smooth" });
  }
}

export default function Portfolio() {
  const [active, setActive] = useState("All");
  const filtersRef = useRef(null);
  const gridRef = useRef(null);
  const skipScrollRef = useRef(true);
  const { data: projects = staticProjects } = useProjects();

  const filtered = useMemo(() => {
    if (active === "All") return projects;
    return projects.filter((p) => p.industry === active || p.services.includes(active));
  }, [active, projects]);

  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    scrollToPortfolioResults(gridRef.current, filtersRef.current);
  }, [active]);

  const handleFilter = (filter) => {
    if (filter === active) return;
    setActive(filter);
  };

  return (
    <div data-testid="page-portfolio" className="pt-[calc(var(--site-header-offset)+2rem)] md:pt-40">
      <section className="px-6 md:px-12 pb-12">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-4">Index — Selected projects ({projects.length})</p>
        <h1 className="font-display uppercase text-[18vw] md:text-[14vw] leading-[0.86] tracking-mega">
          <RevealText>The</RevealText> <RevealText delay={0.06}><em className="font-editorial italic">archive</em>.</RevealText>
        </h1>
        <p className="mt-6 max-w-xl font-body text-lg md:text-xl text-black/70">
          A growing archive of brands. These aren&apos;t just projects. They&apos;re partnerships, experiments, launches, late nights, and ideas brought to life.
        </p>
      </section>

      <section
        ref={filtersRef}
        className="md:sticky md:top-[var(--site-header-height)] z-30 bg-white/95 backdrop-blur-sm border-y border-black/10 py-4 px-6 md:px-12"
        data-testid="portfolio-filters"
      >
        <div className="flex flex-wrap gap-2 md:gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => handleFilter(f)}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 font-body uppercase text-xs tracking-[0.2em] border transition-colors ${active === f ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section ref={gridRef} className="px-6 md:px-12 py-12 md:py-20 scroll-mt-[var(--site-header-height)] md:scroll-mt-[calc(var(--site-header-height)+5rem)]">
        <AnimatePresence mode="popLayout">
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-10 md:gap-y-12"
          >
            {filtered.map((p, i) => {
              const videoIds = getProjectVideoIds(p);
              return (
                <motion.article
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col"
                >
                  <Link to={`/work/${p.slug}`} className="work-tile group relative block aspect-[4/5] w-full bg-black/5" data-testid={`portfolio-tile-${p.slug}`} data-cursor="play">
                    <MediaImage publicId={videoIds[0]} cover={p.cover} videoIds={videoIds} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/70 text-white font-body uppercase text-[10px] tracking-[0.2em] backdrop-blur-sm">
                      <Play className="w-3 h-3" fill="currentColor" /> Film
                    </span>
                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 group-hover:opacity-90" style={{ backgroundColor: p.color }} />
                    <div className="absolute inset-0 p-6 md:p-10 flex flex-col justify-between text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="flex items-start justify-between font-body uppercase text-xs tracking-[0.2em]">
                        <span>{p.industry}</span>
                      </div>
                      <div>
                        <h3 className="font-display text-4xl md:text-6xl uppercase tracking-mega leading-[0.9]">{p.title}</h3>
                        <p className="mt-3 font-editorial italic text-base md:text-lg">{p.tagline}</p>
                      </div>
                    </div>
                  </Link>
                  <div className="mt-4 md:mt-5 flex items-start justify-between gap-4 font-body uppercase text-[10px] md:text-xs tracking-[0.2em] text-black/60">
                    <span className="min-w-0 truncate">{p.title}</span>
                    <span className="flex shrink-0 items-center gap-2">{p.industry} <ArrowUpRight className="w-3.5 h-3.5" /></span>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {filtered.length === 0 && (
          <p className="font-display text-3xl md:text-5xl uppercase text-black/30 mt-20 text-center">Nothing in this drawer yet.</p>
        )}
      </section>
    </div>
  );
}
